/**
 * SiteMap — a site-location map with a pin, keyless.
 *
 * Uses an OpenStreetMap embed (no signup, no API key, attribution baked into
 * the iframe, zero JS bundle). Renders nothing without coordinates. Optional
 * `expandable` shows a control that opens an animated, larger modal view —
 * use it where space is tight (mobile/compact cards).
 *
 * Swap point (ADR-0009 amended): when an Azure Maps key is configured, this
 * is the one place to replace the OSM iframe with an <img> to an Azure Maps
 * Static Image (via a server-side proxy, to keep the key off the client).
 */
import { useEffect, useState } from 'react';
import { Maximize2, X } from 'lucide-react';
import { cn } from '@/lib/cn';

/** OSM embed URL centered on a small bbox with a marker at the point. */
export function osmEmbedUrl(lat: number, lng: number, span = 0.008): string {
  const bbox = [lng - span, lat - span, lng + span, lat + span].join(',');
  return (
    'https://www.openstreetmap.org/export/embed.html' +
    `?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${lat},${lng}`
  );
}

export function SiteMap({
  latitude,
  longitude,
  label = 'Site location',
  className,
  expandable = false,
}: {
  latitude: number | null;
  longitude: number | null;
  label?: string;
  /** Sizing for the inline map; defaults to a compact h-40 thumbnail. */
  className?: string;
  expandable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (latitude == null || longitude == null) return null;
  const src = osmEmbedUrl(latitude, longitude);

  return (
    <>
      <div
        className={cn(
          'relative rounded-xl overflow-hidden border border-line',
          className ?? 'h-40',
        )}
      >
        <iframe src={src} title={label} loading="lazy" className="w-full h-full border-0" />
        {expandable && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Expand map"
            className="absolute top-2 right-2 rounded-md border border-line bg-bg/90 p-1.5 text-ink-dim shadow-sm hover:bg-bg hover:text-ink"
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {open && <MapModal src={src} label={label} onClose={() => setOpen(false)} />}
    </>
  );
}

function MapModal({
  src,
  label,
  onClose,
}: {
  src: string;
  label: string;
  onClose: () => void;
}) {
  // Mount then flip `shown` next frame so the enter transition runs.
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setShown(true));
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label}
      onClick={onClose}
      className={cn(
        'fixed inset-0 z-50 grid place-items-center p-4 transition-colors duration-200',
        shown ? 'bg-black/50' : 'bg-black/0',
      )}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'w-full max-w-3xl overflow-hidden rounded-2xl bg-bg shadow-2xl transition-all duration-200',
          shown ? 'scale-100 opacity-100' : 'scale-95 opacity-0',
        )}
      >
        <div className="flex items-center justify-between border-b border-line px-3 py-2">
          <span className="text-xs text-ink-dim">{label}</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close map"
            className="text-ink-faint hover:text-ink"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <iframe
          src={src}
          title={`${label} (expanded)`}
          loading="lazy"
          className="h-[70vh] w-full border-0"
        />
      </div>
    </div>
  );
}
